/**
 * i18n bootstrap.
 *
 * Two locales for v1: ``en`` (default) and ``it``. Translation files
 * are loaded statically (small, zero round-trip). The active language
 * is read from:
 *
 *   1. user.preferences.language (when logged in)
 *   2. localStorage `tray:i18n:lang`
 *   3. navigator.language (browser default)
 *   4. fallback `en`
 *
 * Calling :func:`setLanguage` updates i18next + persists to
 * localStorage + (if logged in) PATCHes /me/preferences so the choice
 * survives across devices.
 */
import i18n from "i18next";
import { initReactI18next } from "react-i18next";

import en from "./locales/en.json";
import it from "./locales/it.json";

export const SUPPORTED_LANGUAGES = ["en", "it"] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

const LS_KEY = "tray:i18n:lang";

function detectInitial(): SupportedLanguage {
  if (typeof window !== "undefined") {
    const stored = window.localStorage.getItem(LS_KEY);
    if (stored === "en" || stored === "it") return stored;
    const browser = window.navigator.language?.slice(0, 2).toLowerCase();
    if (browser === "it") return "it";
  }
  return "en";
}

void i18n.use(initReactI18next).init({
  resources: {
    en: { translation: en },
    it: { translation: it },
  },
  lng: detectInitial(),
  fallbackLng: "en",
  interpolation: { escapeValue: false }, // React already escapes
  returnNull: false,
});

export function setLanguage(lang: SupportedLanguage): void {
  void i18n.changeLanguage(lang);
  if (typeof window !== "undefined") {
    window.localStorage.setItem(LS_KEY, lang);
  }
}

export function currentLanguage(): SupportedLanguage {
  const l = i18n.language?.slice(0, 2);
  return l === "it" ? "it" : "en";
}

export default i18n;
